import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminHero = () => {
  const [content, setContent] = useState({
    tagline: '',
    title_line1: '',
    title_line2: '',
    description: '',
    cta_primary: '',
    cta_secondary: '',
    image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/hero`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/hero`, { ...content, id: 'hero' }, getAuthHeaders());
      toast.success('Hero section saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-hero">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Hero Section</h1>
          <p className="text-muted-foreground">Edit the main landing area of your website</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-hero-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-border p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Tagline</label>
          <input
            type="text"
            value={content.tagline}
            onChange={(e) => handleChange('tagline', e.target.value)}
            className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            placeholder="e.g., West End Trained Vocal Coach"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title Line 1</label>
            <input
              type="text"
              value={content.title_line1}
              onChange={(e) => handleChange('title_line1', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              placeholder="e.g., Discover Your"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Title Line 2 (Accent)</label>
            <input
              type="text"
              value={content.title_line2}
              onChange={(e) => handleChange('title_line2', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              placeholder="e.g., True Voice"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <textarea
            value={content.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
            placeholder="Main description text..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Primary Button Text</label>
            <input
              type="text"
              value={content.cta_primary}
              onChange={(e) => handleChange('cta_primary', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              placeholder="e.g., Book a Lesson"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Secondary Button Text</label>
            <input
              type="text"
              value={content.cta_secondary}
              onChange={(e) => handleChange('cta_secondary', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              placeholder="e.g., Meet Kerry"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Hero Image URL</label>
          <input
            type="url"
            value={content.image_url}
            onChange={(e) => handleChange('image_url', e.target.value)}
            className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            placeholder="https://..."
          />
          {content.image_url && (
            <div className="mt-4">
              <img src={content.image_url} alt="Preview" className="max-w-xs rounded border" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHero;
